import React from 'react';
import { motion } from 'framer-motion';
import { TypingUser } from '../../services/typingIndicatorService';

interface TypingIndicatorProps {
  typingUsers: TypingUser[];
  className?: string;
}

const TypingIndicator: React.FC<TypingIndicatorProps> = ({ typingUsers, className = '' }) => {
  console.log('🎭 TypingIndicator rendered with:', {
    typingUsersCount: typingUsers.length,
    typingUsers: typingUsers.map(u => ({
      displayName: u.displayName,
      draftContent: u.draftContent,
      hasContent: !!(u.draftContent && u.draftContent.trim())
    }))
  });
  
  if (typingUsers.length === 0) return null;

  const getTypingText = () => {
    if (typingUsers.length === 1) {
      const user = typingUsers[0];
      if (user.draftContent && user.draftContent.trim()) {
        return `${user.displayName} is typing: "${user.draftContent}"`;
      }
      return `${user.displayName} is typing...`;
    } else if (typingUsers.length === 2) {
      return `${typingUsers[0].displayName} and ${typingUsers[1].displayName} are typing...`;
    } else {
      return `${typingUsers[0].displayName} and ${typingUsers.length - 1} others are typing...`;
    }
  };

  const hasLiveContent = typingUsers.length === 1 && typingUsers[0].draftContent && typingUsers[0].draftContent.trim();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className={`px-4 py-2 ${className}`}
    >
      {hasLiveContent ? (
        // Enhanced UI for live message preview
        <div className="bg-purple-100 rounded-lg p-3 border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            {/* Typing animation dots */}
            <div className="flex items-center gap-1">
              <motion.div
                className="w-1.5 h-1.5 bg-purple-500 rounded-full"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0 }}
              />
              <motion.div
                className="w-1.5 h-1.5 bg-purple-500 rounded-full"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
              />
              <motion.div
                className="w-1.5 h-1.5 bg-purple-500 rounded-full"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
              />
            </div>
            <span className="text-xs text-purple-600 font-medium">
              {typingUsers[0].displayName} is typing:
            </span>
          </div>
          
          {/* Live message content */}
          <div className="bg-white rounded px-3 py-2 border-l-2 border-purple-400">
            <p className="text-gray-800 text-sm">
              "{typingUsers[0].draftContent}"
              <motion.span
                className="inline-block w-0.5 h-4 bg-purple-500 ml-1"
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              />
            </p>
          </div>
        </div>
      ) : (
        // Standard typing indicator
        <div className="flex items-center gap-2">
          {/* Typing animation dots */}
          <div className="flex items-center gap-1">
            <motion.div
              className="w-2 h-2 bg-purple-500 rounded-full"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity, delay: 0 }}
            />
            <motion.div
              className="w-2 h-2 bg-purple-500 rounded-full"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
            />
            <motion.div
              className="w-2 h-2 bg-purple-500 rounded-full"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
            />
          </div>
          
          {/* Typing text */}
          <span className="text-sm text-purple-600 italic">
            {getTypingText()}
          </span>
        </div>
      )}
    </motion.div>
  );
};

export default TypingIndicator;
